import { useState, type FormEvent } from "react";
import BackgroundLayer from "./BackgroundLayer";
import Wordmark from "./Wordmark";
import { signInWithMagicLink } from "../lib/supabaseAuth";
import { useTheme } from "../lib/theme/ThemeProvider";

interface Props {
  defaultEmail?: string;
}

type LoginStatus = "idle" | "sending" | "sent" | "error";

/**
 * Magic-link login surface. Renders when there's no Supabase session yet.
 * Supabase Auth + RLS is the sole gate. Copy + imagery follow the active theme.
 */
function MagicLinkLogin({ defaultEmail = "" }: Props) {
  const { themeId } = useTheme();
  const frontier = themeId === "frontier";
  const [email, setEmail] = useState(defaultEmail);
  const [status, setStatus] = useState<LoginStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const titleA = frontier ? "RIDE FOR" : "SUMMON";
  const titleB = frontier ? "THE LAW." : "THE RAVEN.";
  const body = frontier
    ? "You're past the gate. To open the marshal's office, we'll wire a one-time link to your inbox. Click it to ride in."
    : "You've passed the gate. To open the war room, the raven will deliver a one-time link to your inbox. Click it to begin.";
  const sentKicker = frontier ? "// wire sent" : "// raven dispatched";
  const cta = frontier ? "Wire the marshal →" : "Send the raven →";
  const pending = frontier ? "wiring..." : "dispatching...";

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus("sending");
    setError(null);
    const result = await signInWithMagicLink(email);
    if (result.ok) {
      setStatus("sent");
    } else {
      setStatus("error");
      setError(result.error ?? "Unknown error sending magic link");
    }
  }

  return (
    <div className="relative min-h-screen text-bone">
      <BackgroundLayer variant="modes" overlay="heavy" />

      <main className="relative z-10 mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center px-6">
        <Wordmark size="lg" className="mb-8" />

        <p className="mb-3 font-display text-base font-light uppercase tracking-[0.18em] text-bone-mute">
          // session // not yet established
        </p>

        <h1
          className="text-center font-display text-4xl font-light leading-tight tracking-tight md:text-5xl"
          style={{ textShadow: "0 2px 24px rgba(0,0,0,0.7)" }}
        >
          <span className="block text-bone">{titleA}</span>
          <span
            className="block text-ember-bright"
            style={{
              textShadow:
                "0 0 32px rgb(var(--c-ember-glow) / 0.55), 0 2px 24px rgba(0,0,0,0.6)",
            }}
          >
            {titleB}
          </span>
        </h1>

        <p
          className="mt-6 max-w-md text-center font-display text-sm leading-relaxed text-bone-dim"
          style={{ textShadow: "0 1px 12px rgba(0,0,0,0.7)" }}
        >
          {body}
        </p>

        {status === "sent" ? (
          <div className="mt-10 w-full max-w-md rounded border border-ember-bright/40 bg-ember/15 px-6 py-5 text-center backdrop-blur-sm">
            <p className="font-display text-base font-light uppercase tracking-[0.16em] text-ember-bright">
              {sentKicker}
            </p>
            <p className="mt-3 font-display text-sm text-bone-dim">
              Check <span className="font-display text-bone">{email}</span>. The link
              expires in 60 minutes.
            </p>
            <button
              type="button"
              onClick={() => {
                setStatus("idle");
                setError(null);
              }}
              className="mt-4 font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute hover:text-bone"
            >
              ← send another
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-10 w-full max-w-md space-y-4">
            <label className="block">
              <span className="font-display text-base font-light uppercase tracking-[0.16em] text-bone-mute">
                // operator email
              </span>
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full border border-steel-dark bg-iron/70 px-4 py-3 font-display text-sm text-bone outline-none backdrop-blur-sm transition-all focus:border-ember-bright/60 focus:shadow-ember-soft"
                placeholder="you@firm.com"
              />
            </label>

            {error ? (
              <p className="font-display text-base font-light uppercase tracking-[0.12em] text-ember-bright">
                // {error}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={status === "sending"}
              className="w-full border border-ember-bright/60 bg-ember/40 px-6 py-3 font-display text-base font-light uppercase tracking-[0.16em] text-bone transition-colors hover:bg-ember/60 hover:shadow-ember-glow disabled:cursor-wait disabled:opacity-60"
            >
              {status === "sending" ? pending : cta}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}

export default MagicLinkLogin;
